import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {Button, Grid, TextField, useTheme} from "@mui/material";
import Stack from "@mui/material/Stack";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useTranslation} from "react-i18next";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useNavigate} from "react-router-dom";
import {usePaOrdersActions} from "../../service/usePaOrdersActions";
import EmptyState from "../../../../util/component/EmptyState";
import {LibraryAddRounded} from "@mui/icons-material";
import SectionHeader from "../../../../util/component/SectionHeader";
import AdvancedFilter from "../../component/AdvancedFilter";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import * as Yup from "yup";
import {INTEGER_REG_EXP} from "../../component/Constants";
import PaOrderDetailModal from "./modals/PaOrderDetailModal";
import {ValueFormatterParams} from "ag-grid-community";

const PaOrdersListPage = () => {

    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'paOrders'});
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const {getOrders} = usePaOrdersActions();
    const [openDetail, setOpenDetail] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState({})
    const hasScope = useHasScope;
    const filterDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        cig: '', cup: '', orderId: '', businessCode:''
    };

    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const [tableShouldUpdate, setTableShouldUpdate] = useState(false);

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "cig",
            headerName: t('cigNumber'),
            unSortIcon: true,
            tooltipField: "cigNumber",
            pinned: 'left',
            maxWidth: 160
        },
        {
            field: "cup",
            headerName: t('cupNumber'),
            unSortIcon: true,
            tooltipField: "cupNumber",
        },

        {
            field: "orderId",
            headerName: t('orderId'),
            unSortIcon: true,
            tooltipField: "orderId",
        },
        {
            field: "description",
            headerName: t('description'),
            unSortIcon: true,
            tooltipField: "description",
        },
        {
            field: "businessCode",
            headerName: t('businessCode'),
            unSortIcon: true,
            tooltipField: "businessCode",
            valueFormatter: (params: ValueFormatterParams) => params.value ??  '--'
        },
        {
            field: "_actions",
            headerName: '',
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            maxWidth: 160,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip auth={'SCOPE_SM_UPDATE_PAORDER'} type={ActionTypes.edit} callback={() => {
                            window.open(`/salesmarketing/paorderupdate/${params.data.id}`, "_blank");
                        }}/>
                        <ActionButtonWithTooltip auth={'SCOPE_SM_VIEW_PAORDER_DETAIL'} type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedOrder(params.data);
                        }}/>
                    </Stack>
                );
            }
        }
    ];

    function createDatasource(filters: any, api: GridApi) {
        function getSort(params:IServerSideGetRowsParams){
            return params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId === 'businessCode' ?`businessConvention.${params.request.sortModel[0].colId}`: params.request.sortModel[0].colId
        }

        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = getSort(params);
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                try {
                    let data = await getOrders(filterTemp);
                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                    if (data.totalElements === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                    }
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        noRowsOverlayComponentParams: {title: t('noRows')}
    };

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    useEffect(() => {
        if (tableShouldUpdate) {
            gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
            setTableShouldUpdate(false);
        }
    }, [tableShouldUpdate]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    const toolbar = {
        renderActions: () =>
            <>{hasScope('SCOPE_SM_CREATE_PAORDER') ?
                <Button fullWidth  size={"small"} startIcon={<LibraryAddRounded/>} onClick={() => {
                    navigate('/salesmarketing/paordercreate')
                }}>
                    {t('buttons.createOrder')}
                </Button> : <></>
            }
            </>
    };

    const validationObj = {
        cig: Yup.string().nullable(),
        cup: Yup.string().nullable(),
        orderId: Yup.string().nullable(),
        businessCode: Yup.string().nullable()
    }

    const renderCigNumber = (field: any) => (
        <TextField
            fullWidth
            label={t('cigNumber')}
            value={field.value}
            onChange={field.onChange}
        />
    );
    const renderCupNumber = (field: any) => (
        <TextField
            fullWidth
            label={t('cupNumber')}
            value={field.value}
            onChange={field.onChange}
        />
    );

    const renderOrderId = (field: any) => (
        <TextField
            fullWidth
            label={t('orderId')}
            value={field.value}
            onChange={field.onChange}
        />
    );
    const renderBusinessCode = (field: any) => (
        <TextField
            fullWidth
            label={t('businessCode')}
            value={field.value}
            onChange={field.onChange}
        />
    );

    const fields = {
        cig: renderCigNumber,
        cup: renderCupNumber,
        orderId: renderOrderId,
        businessCode: renderBusinessCode
    }

    return (
        <>
            <SectionHeader title={t('listHeader.title')}
                           subtitle={t('listHeader.subTitle')}/>

            <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable
                        gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={(params) => {
                            params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                        }}
                    />
                </Grid>
            </Grid>
            <PaOrderDetailModal open={openDetail} order={selectedOrder} handleClose={() => {
                setOpenDetail(false)
            }}/>
        </>
    );
}
export default PaOrdersListPage;